import { z } from "zod/v4";

export const ShippingModelSchema = z.enum(["legacy", "markets"]);
export type ShippingModel = z.infer<typeof ShippingModelSchema>;

export const MarketShippingOptionTypeSchema = z.enum([
  "flat_rate",
  "value_based",
  "weight_based",
  "carrier_calculated",
]);

export type MarketShippingOptionType = z.infer<
  typeof MarketShippingOptionTypeSchema
>;

/** A Shopify Market GID. Fixtures use non-numeric ids, so only the shape is checked. */
export const MarketGidSchema = z
  .string()
  .regex(/^gid:\/\/shopify\/Market\/[^/]+$/);

const marketSelectionFields = {
  marketId: MarketGidSchema,
  optionId: z.string().min(1),
  optionType: MarketShippingOptionTypeSchema,
};

export const MarketShippingSelectionSchema = z.discriminatedUnion("kind", [
  z.object({
    ...marketSelectionFields,
    kind: z.literal("option"),
    rateGroupId: z.undefined().optional(),
    rateId: z.undefined().optional(),
  }),
  z.object({
    ...marketSelectionFields,
    kind: z.literal("rate_group"),
    rateGroupId: z.string().min(1),
    rateId: z.undefined().optional(),
  }),
  z.object({
    ...marketSelectionFields,
    kind: z.literal("rate"),
    optionType: z.enum(["flat_rate", "value_based", "weight_based"]),
    rateGroupId: z.string().min(1),
    rateId: z.string().min(1),
  }),
  z.object({
    ...marketSelectionFields,
    kind: z.literal("free_shipping_threshold"),
    rateGroupId: z.undefined().optional(),
    rateId: z.undefined().optional(),
  }),
]);

export type MarketShippingSelection = z.infer<
  typeof MarketShippingSelectionSchema
>;

export const MarketShippingBackupTargetSchema = z.discriminatedUnion("kind", [
  ...MarketShippingSelectionSchema.options,
  z.object({
    kind: z.literal("unresolved_option"),
    marketId: marketSelectionFields.marketId,
    optionType: MarketShippingOptionTypeSchema,
    optionId: z.undefined().optional(),
    rateGroupId: z.undefined().optional(),
    rateId: z.undefined().optional(),
  }),
]);

export type MarketShippingBackupTarget = z.infer<
  typeof MarketShippingBackupTargetSchema
>;

export const ShippingMutationStatusSchema = z.enum([
  "prepared",
  "applied",
  "restoring",
  "restored",
  "conflict",
  "uncertain",
  /**
   * Intelligems gave up: the change stays in the merchant's Shopify store and
   * will never be restored. Distinct from `restored` on purpose — the backup
   * UI must not claim shipping was put back when it was not, and retention
   * must never purge an abandoned row's replacement IDs, which describe an
   * artifact that is still live.
   */
  "abandoned",
]);

/** Why a change was abandoned, and by whom. Owners move in here so a settled
 * row still has none, without losing who held it. */
export const MarketShippingAbandonmentSchema = z.object({
  ts: z.number(),
  source: z.enum(["merchant", "support"]),
  actorUserId: z.string().min(1).optional(),
  reason: z.string().min(1),
  previousStatus: ShippingMutationStatusSchema,
  previousConflict: z.string().optional(),
  ownersAtAbandonment: z.array(z.string().min(1)),
  /** The row the merchant acted on, when this one was abandoned alongside it. */
  cascadedFrom: z.string().min(1).optional(),
});

export type MarketShippingAbandonment = z.infer<
  typeof MarketShippingAbandonmentSchema
>;

export const MarketShippingBackupSnapshotSchema = z
  .object({
    shippingModel: z.literal("markets"),
    schemaVersion: z.literal(1),
    status: ShippingMutationStatusSchema,
    original: z.record(z.string(), z.json()),
    expected: z.record(z.string(), z.json()),
    changedFields: z.array(z.string().min(1)).min(1),
    replacementIds: z.record(z.string(), z.string().min(1)),
    conflict: z.string().optional(),
    abandonment: MarketShippingAbandonmentSchema.pick({
      ts: true,
      source: true,
      reason: true,
    }).optional(),
  })
  .superRefine((snapshot, context) => {
    for (const field of snapshot.changedFields) {
      if (!(field in snapshot.original) || !(field in snapshot.expected)) {
        context.addIssue({
          code: "custom",
          path: ["changedFields"],
          message:
            "Each changed field requires its original and expected values",
        });
      }
    }
  });

export type MarketShippingBackupSnapshot = z.infer<
  typeof MarketShippingBackupSnapshotSchema
>;

const marketShippingId = z.string().min(1);
const marketShippingObject = z.record(z.string(), z.json());

export const MarketShippingChangeTargetSchema = z.object({
  optionId: z.string(),
  optionType: MarketShippingOptionTypeSchema,
  field: z.enum([
    "isActive",
    "freeDeliveryMinimumValue",
    "group",
    "rate",
    "option",
  ]),
  groupId: marketShippingId.optional(),
  rateId: marketShippingId.optional(),
});

export type MarketShippingChangeTarget = z.infer<
  typeof MarketShippingChangeTargetSchema
>;

export const MarketShippingPlannedChangeSchema = z.object({
  target: MarketShippingChangeTargetSchema,
  original: z.json(),
  expected: z.json(),
  input: marketShippingObject,
  inverse: marketShippingObject,
  conditions: z.array(
    z.object({
      groupId: marketShippingId,
      value: z.object({
        collectionIds: z.array(marketShippingId).nullable(),
        originLocationIds: z.array(marketShippingId),
      }),
    })
  ),
});

export type MarketShippingPlannedChange = z.infer<
  typeof MarketShippingPlannedChangeSchema
>;

export const MarketShippingMutationRecordSchema =
  MarketShippingPlannedChangeSchema.extend({
    shippingModel: z.literal("markets"),
    schemaVersion: z.literal(1),
    id: marketShippingId,
    orgId: marketShippingId,
    marketId: marketShippingId,
    originalOwnerId: marketShippingId,
    ownerHistory: z.array(marketShippingId).min(1),
    owners: z.array(marketShippingId),
    status: ShippingMutationStatusSchema,
    replacementIds: z.record(marketShippingId, marketShippingId),
    beforeIds: z.array(marketShippingId),
    backedUpTs: z.number().optional(),
    restoredTs: z.number().optional(),
    restorationStarted: z.boolean().optional(),
    appliedVerified: z.boolean().optional(),
    conflict: z.string().optional(),
    placementKey: marketShippingId.optional(),
    selectionKey: marketShippingId.optional(),
    abandonment: MarketShippingAbandonmentSchema.optional(),
    sequence: z.number().int().positive(),
  }).superRefine((record, context) => {
    if (
      !record.ownerHistory.includes(record.originalOwnerId) ||
      record.owners.some((owner) => !record.ownerHistory.includes(owner))
    ) {
      context.addIssue({
        code: "custom",
        path: ["ownerHistory"],
        message: "Owner history must retain original and active owners",
      });
    }
    if (
      ["restored", "abandoned"].includes(record.status) &&
      record.owners.length
    ) {
      context.addIssue({
        code: "custom",
        path: ["owners"],
        message: "Settled changes cannot have active owners",
      });
    }
    if (
      (record.status === "abandoned") !==
      (record.abandonment !== undefined)
    ) {
      context.addIssue({
        code: "custom",
        path: ["abandonment"],
        message: "Abandoned changes require abandonment provenance",
      });
    }
    if (record.target.field !== "option" && !record.target.optionId) {
      context.addIssue({
        code: "custom",
        path: ["target", "optionId"],
        message: "Existing target requires an option ID",
      });
    }
    if (
      ["group", "rate"].includes(record.target.field) &&
      !record.target.groupId
    ) {
      context.addIssue({
        code: "custom",
        path: ["target", "groupId"],
        message: "Group target requires a group ID",
      });
    }
    if (record.target.field === "rate" && !record.target.rateId) {
      context.addIssue({
        code: "custom",
        path: ["target", "rateId"],
        message: "Rate target requires a rate ID",
      });
    }
  });

export type MarketShippingMutationRecord = z.infer<
  typeof MarketShippingMutationRecordSchema
>;

const MarketShippingMutationOwnerPlanBaseSchema = z.object({
  id: marketShippingId,
  shippingModel: z.literal("markets"),
  schemaVersion: z.literal(1),
  orgId: marketShippingId,
  ownerId: marketShippingId,
  status: z.enum([
    "prepared",
    "applying",
    "applied",
    "compensating",
    "restored",
    "conflict",
    /**
     * Applied and intact in Shopify, but the experience's selections changed
     * in the database underneath it. Nothing is broken, so it holds no other
     * owner back; this owner must pause and restart before its coverage can
     * change, and the plan returns to `applied` if the selections match
     * again.
     */
    "stale",
  ]),
  selections: z.array(MarketShippingSelectionSchema),
  sourceSelections: z.array(MarketShippingSelectionSchema).optional(),
  placements: z.array(
    z.object({
      marketId: marketShippingId,
      currency: marketShippingId,
      carrierServiceId: marketShippingId,
      collectionIds: z.array(marketShippingId).nullable(),
      originLocationIds: z.array(marketShippingId),
      /** Carrier `service_name`s this placement admits (Shopify matches a
       * carrier rate group's services by name); empty or absent admits every
       * service the carrier returns. */
      serviceNames: z.array(z.string().min(1)).optional(),
    })
  ),
  operations: z.array(
    z.object({
      id: marketShippingId,
      marketId: marketShippingId,
      selectionKey: marketShippingId,
      change: MarketShippingPlannedChangeSchema,
    })
  ),
  obsoletePlacementIds: z.array(marketShippingId),
  conflict: z.string().optional(),
});

export const MarketShippingMutationOwnerPlanSchema =
  MarketShippingMutationOwnerPlanBaseSchema.extend({
    rollback: z
      .object({
        previousPlan: MarketShippingMutationOwnerPlanBaseSchema,
        protectedRowIds: z.array(marketShippingId),
      })
      .optional(),
  });

export type MarketShippingMutationOwnerPlan = z.infer<
  typeof MarketShippingMutationOwnerPlanSchema
>;

export const MarketShippingCarrierRegistrationStatusSchema = z.enum([
  "prepared",
  "applied",
  "restoring",
  "restored",
  "uncertain",
  /**
   * More than one carrier outside the pre-create snapshot matches the
   * registration, so Intelligems cannot tell which one it made. It never
   * guesses; the merchant removes the duplicates and the next acquire
   * resolves the survivor.
   */
  "conflict",
]);

/** The one carrier service Intelligems registers per shop, shared by every
 * owner, with the snapshot that identifies the carrier a create produced. */
export const MarketShippingCarrierRegistrationSchema = z
  .object({
    orgId: marketShippingId,
    carrierId: z.number().int().positive().nullable(),
    name: z.string().min(1),
    callbackUrl: z.string().min(1),
    owners: z.array(marketShippingId),
    managed: z.boolean(),
    status: MarketShippingCarrierRegistrationStatusSchema,
    /** Carrier IDs present before the create, so the new one can be told apart. */
    beforeIds: z.array(z.number().int()),
    activation: z.enum(["pending", "confirmed"]).optional(),
    /** Why the registration cannot resolve on its own; present exactly when
     * the status is `conflict`. */
    conflict: z.string().min(1).optional(),
  })
  .superRefine((registration, context) => {
    if (
      (registration.status === "conflict") !==
      (registration.conflict !== undefined)
    ) {
      context.addIssue({
        code: "custom",
        path: ["conflict"],
        message: "Conflicting registrations require a conflict reason",
      });
    }
    if (registration.status === "restored" && registration.owners.length) {
      context.addIssue({
        code: "custom",
        path: ["owners"],
        message: "A released registration cannot have owners",
      });
    }
  });

export type MarketShippingCarrierRegistration = z.infer<
  typeof MarketShippingCarrierRegistrationSchema
>;

export const MarketShippingModelRecoverySchema = z.object({
  reason: z.string().min(1),
  backupIds: z.array(z.string().min(1)),
  experienceIds: z.array(z.string().min(1)),
});

export type MarketShippingModelRecovery = z.infer<
  typeof MarketShippingModelRecoverySchema
>;

/** What the shop was last alerted about, so a store that stays broken is
 * reported once a day rather than once per reconciliation pass. */
export const MarketShippingAlertStateSchema = z.object({
  /** Digest of the conflicts and outstanding backups the last alert described. */
  hash: z.string().min(1),
  notifiedTs: z.number(),
});

export type MarketShippingAlertState = z.infer<
  typeof MarketShippingAlertStateSchema
>;

/**
 * `upgrade` prepares legacy → Markets, `downgrade` prepares Markets → legacy,
 * `cancel_upgrade` unwinds a prepared upgrade (restores legacy placements and
 * unlatches `prepared`).
 */
export const MarketShippingTransitionKindSchema = z.enum([
  "upgrade",
  "downgrade",
  "cancel_upgrade",
]);

export type MarketShippingTransitionKind = z.infer<
  typeof MarketShippingTransitionKindSchema
>;

export const MarketShippingTransitionStatusSchema = z.enum([
  "queued",
  "running",
  "succeeded",
  "failed",
]);

export type MarketShippingTransitionStatus = z.infer<
  typeof MarketShippingTransitionStatusSchema
>;

/**
 * One merchant-requested shipping-model transition (an upgrade or downgrade
 * preparation, or the cancellation of a prepared upgrade) and how far it
 * got. The request only records `queued` and enqueues the work; the queue
 * lane moves it to `running`, then to `succeeded` with the result or
 * `failed` with the error. A `succeeded` transition may still carry
 * conflicts: the work finished, but recovery has to clear before Shopify is
 * switched (or, for a cancellation, before the preparation is unlatched).
 */
export const MarketShippingTransitionSchema = z
  .object({
    kind: MarketShippingTransitionKindSchema,
    status: MarketShippingTransitionStatusSchema,
    requestedAt: z.iso.datetime(),
    startedAt: z.iso.datetime().nullable(),
    finishedAt: z.iso.datetime().nullable(),
    endedExperienceIds: z.array(z.string().min(1)),
    unresolvedBackups: z.array(z.string().min(1)),
    conflicts: z.array(z.string().min(1)),
    error: z.string().min(1).nullable(),
  })
  .superRefine((transition, context) => {
    const mustHaveStarted =
      transition.status === "running" || transition.status === "succeeded";
    if (
      (mustHaveStarted && transition.startedAt === null) ||
      (transition.status === "queued" && transition.startedAt !== null)
    ) {
      context.addIssue({
        code: "custom",
        path: ["startedAt"],
        message:
          "A running or succeeded transition has a start time; a queued one does not",
      });
    }
    const finished =
      transition.status === "succeeded" || transition.status === "failed";
    if ((transition.finishedAt !== null) !== finished) {
      context.addIssue({
        code: "custom",
        path: ["finishedAt"],
        message: "A transition has a finish time exactly once it is terminal",
      });
    }
    if ((transition.error !== null) !== (transition.status === "failed")) {
      context.addIssue({
        code: "custom",
        path: ["error"],
        message: "A failed transition carries its error, and only a failed one",
      });
    }
  });

export type MarketShippingTransition = z.infer<
  typeof MarketShippingTransitionSchema
>;

/**
 * The answer to a prepare-upgrade, prepare-downgrade or cancel-upgrade
 * request. `accepted` means the work is queued (or was already queued or
 * running); progress is then read from `ShippingCapabilities.transition`,
 * never from this body.
 */
export const MarketShippingTransitionRequestResultSchema = z.object({
  accepted: z.boolean(),
  blockedReason: z.string().min(1).nullable(),
  transition: MarketShippingTransitionSchema.nullable(),
});

export type MarketShippingTransitionRequestResult = z.infer<
  typeof MarketShippingTransitionRequestResultSchema
>;

/** The upgrade / model-transition state, one document per shop. */
export const MarketShippingUpgradeStateSchema = z.object({
  modelRecovery: MarketShippingModelRecoverySchema.optional(),
  prepared: z.boolean(),
  preparedAt: z.string().nullable(),
  marketsDetected: z.boolean(),
  /** A newly detected Markets switch still needs its live-test alert check. */
  migrationAlertPending: z.boolean().optional(),
  requireNewSelections: z.boolean(),
  alerting: MarketShippingAlertStateSchema.optional(),
  /** The latest merchant-requested model transition, until a cancel clears it. */
  transition: MarketShippingTransitionSchema.optional(),
});

export type MarketShippingUpgradeState = z.infer<
  typeof MarketShippingUpgradeStateSchema
>;

export interface ShippingMaintenanceResult {
  ready: boolean;
  unresolvedBackups: string[];
  conflicts: string[];
}
